import { FC, memo, useMemo, useRef } from 'react';
import { FilledInput, FormControl, Input, InputLabel, OutlinedInput, SelectProps } from '@mui/material';

import { SelectFormControlProps } from './types';
import { getArrayMenuItemsToRenderSelect, getMapMenuItemsToRenderSelect } from './helpers';
import { ItemText, SelectStyled } from './styles';

export const SelectFormControl: FC<SelectFormControlProps> = memo((props) => {
  const {
    label,
    variant,
    disabled,
    inputProps = {},
    selectProps = {} as SelectProps,
    labelProps = {},
    selectedOption,
    options,
    error,
    isNoneOptionShown = false,
    id = 'input',
    onOptionSelect,
    customOptionsMapper,
    markdown,
    className,
  } = props;
  const inputLabelRef = useRef(null);

  const input =
    variant === 'outlined' ? (
      <OutlinedInput label={label} name={label} id={id} {...inputProps} />
    ) : variant === 'filled' ? (
      <FilledInput name={label} id={id} {...inputProps} />
    ) : (
      <Input name={label} id={id} {...inputProps} />
    );

  const optionsLength = useMemo<number>(() => {
    if (options instanceof Array) {
      return options.length;
    }

    if (options instanceof Map) {
      return options.size;
    }

    return 0;
  }, [options]);

  const items = useMemo<React.JSX.Element[]>(() => {
    let menuItems: React.JSX.Element[] = [
      <ItemText key={0} value=''>
        No options to select
      </ItemText>,
    ];

    if (optionsLength === 0) {
      return menuItems;
    }

    if (options instanceof Array) {
      menuItems = getArrayMenuItemsToRenderSelect(options, customOptionsMapper);
    } else if (options instanceof Map) {
      menuItems = getMapMenuItemsToRenderSelect(options, customOptionsMapper);
    }

    return !isNoneOptionShown
      ? menuItems
      : [
          <ItemText key={-1} value=''>
            -
          </ItemText>,
          ...menuItems,
        ];
  }, [optionsLength, options, isNoneOptionShown, customOptionsMapper]);

  return (
    <FormControl
      className={className}
      size={selectProps.size || 'medium'}
      error={error}
      variant={variant}
      disabled={disabled || optionsLength === 0}
      fullWidth
    >
      {label && (
        <InputLabel ref={inputLabelRef} htmlFor='currency' {...labelProps}>
          {label}
        </InputLabel>
      )}

      <SelectStyled
        {...selectProps}
        value={selectedOption}
        onChange={onOptionSelect}
        input={input}
        data-testid={label ? `${label.toLowerCase()}-select-label` : `default-select-label`}
      >
        {items}
      </SelectStyled>
      {markdown}
    </FormControl>
  );
});
